import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOut, Trophy, Users, ChalkboardTeacher, CalendarBlank, MegaphoneSimple, ClipboardText, Images, VideoCamera, ChartBar, Newspaper, ShoppingBag, Handshake, GearSix, Money, Wallet, CalendarCheck, FileText, UserGear, Timer, CloudArrowUp, UserCheck, IdentificationBadge, GitMerge, Key, Check, HandCoins } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import ChangeCredentialsModal from "../components/ChangeCredentialsModal";
import NotificationsBell from "../components/admin/NotificationsBell";
import ResourceTab from "../components/admin/ResourceTab";
import InquiriesTab from "../components/admin/InquiriesTab";
import GalleryTab from "../components/admin/GalleryTab";
import StatsTab from "../components/admin/StatsTab";
import SettingsTab from "../components/admin/SettingsTab";
import ParentPortalTab from "../components/admin/ParentPortalTab";
import ReregistrationTab from "../components/admin/ReregistrationTab";
import StaffAccountsTab from "../components/admin/StaffAccountsTab";
import FinanceDuesTab from "../components/admin/FinanceDuesTab";
import PaymentSubmissionsTab from "../components/admin/PaymentSubmissionsTab";
import FinanceReportTab from "../components/admin/FinanceReportTab";
import AttendanceTab from "../components/admin/AttendanceTab";
import FundSupportTab from "../components/admin/FundSupportTab";
import ReportCardsTab from "../components/admin/ReportCardsTab";
import ParentAchievementsReview from "../components/admin/ParentAchievementsReview";
import EventMergeManager from "../components/admin/EventMergeManager";
import TimeRecordsTab from "../components/admin/TimeRecordsTab";
import RankingsTab from "../components/admin/RankingsTab";
import BackupTab from "../components/admin/BackupTab";
import CoachActivityTab from "../components/admin/CoachActivityTab";
import AdminAthletesTab from "../components/admin/AdminAthletesTab";
import DuplicatesTab from "../components/admin/DuplicatesTab";

const TAB_GROUPS = [
    {
        id: "operasional",
        label: "Operasional & Akun",
        tabs: [
            { id: "parents", label: "Portal Ortu", icon: Users, roles: ["admin"] },
            { id: "atlet-aktif", label: "Siswa Aktif", icon: IdentificationBadge, roles: ["admin", "coach", "finance"] },
            { id: "data-ganda", label: "Data Ganda", icon: GitMerge, roles: ["admin"] },
            { id: "daftar-ulang", label: "Akun Pendaftar", icon: ClipboardText, roles: ["admin"] },
            { id: "staff", label: "Akun Staff", icon: UserGear, roles: ["admin"] },
            { id: "iuran", label: "Iuran", icon: Money, roles: ["admin", "finance"] },
            { id: "pembayaran", label: "Konfirmasi Bayar", icon: Check, roles: ["admin", "finance"] },
            { id: "keuangan", label: "Keuangan", icon: Wallet, roles: ["admin", "finance"] },
            { id: "absensi", label: "Absensi", icon: CalendarCheck, roles: ["admin", "coach", "finance"] },
            { id: "uang-latihan", label: "Uang Latihan", icon: HandCoins, roles: ["admin", "finance"] },
            { id: "rapot", label: "Rapot", icon: FileText, roles: ["admin", "coach"] },
            { id: "catatan-waktu", label: "Catatan Waktu", icon: Timer, roles: ["admin", "coach"] },
            { id: "ranking", label: "Ranking", icon: Trophy, roles: ["admin", "coach"] },
            { id: "kehadiran-pelatih", label: "Kehadiran Pelatih", icon: UserCheck, roles: ["admin"] },
            { id: "backup", label: "Backup Data", icon: CloudArrowUp, roles: ["admin"] },
        ],
    },
    {
        id: "website",
        label: "Tampilan Website",
        tabs: [
            { id: "prestasi", label: "Prestasi", icon: Trophy, roles: ["admin"] },
            { id: "atlet", label: "Atlet", icon: Users, roles: ["admin"] },
            { id: "coach", label: "Coach", icon: ChalkboardTeacher, roles: ["admin"] },
            { id: "jadwal", label: "Jadwal", icon: CalendarBlank, roles: ["admin"] },
            { id: "galeri", label: "Galeri", icon: Images, roles: ["admin"] },
            { id: "video", label: "Video", icon: VideoCamera, roles: ["admin"] },
            { id: "berita", label: "Berita", icon: Newspaper, roles: ["admin"] },
            { id: "gear", label: "Link Rekomendasi", icon: ShoppingBag, roles: ["admin"] },
            { id: "sponsor", label: "Sponsor", icon: Handshake, roles: ["admin"] },
            { id: "inquiries", label: "Pengajuan", icon: ClipboardText, roles: ["admin"] },
            { id: "pengumuman", label: "Pengumuman", icon: MegaphoneSimple, roles: ["admin"] },
            { id: "stats", label: "Statistik", icon: ChartBar, roles: ["admin"] },
            { id: "settings", label: "Pengaturan", icon: GearSix, roles: ["admin"] },
        ],
    },
];

const TABS = TAB_GROUPS.flatMap((group) => group.tabs);

const MEDAL_OPTIONS = [
    { value: "gold", label: "Emas" },
    { value: "silver", label: "Perak" },
    { value: "bronze", label: "Perunggu" },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const visibleTabs = TABS.filter((item) => item.roles.includes(user.role));
    const [tab, setTab] = useState(() => visibleTabs[0]?.id || "prestasi");
    const [achievementRefreshKey, setAchievementRefreshKey] = useState(0);
    const [credentialsOpen, setCredentialsOpen] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("drive") === "connected") {
            toast.success("Google Drive berhasil terhubung");
            window.history.replaceState({}, "", "/admin");
            setTab("backup");
        }
    }, []);

    useEffect(() => {
        if (!visibleTabs.some((item) => item.id === tab)) {
            setTab(visibleTabs[0]?.id || "prestasi");
        }
    }, [user.role]);

    const doLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#FBF9F5] text-neutral-900" data-testid="admin-dashboard">
            <header className="border-b border-racing-line sticky top-0 bg-white/90 backdrop-blur-xl z-40">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandLogo className="w-8 h-8" testid="admin-panel-logo" />                        <span className="font-display uppercase tracking-wide text-sm">Panel Admin</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:block text-xs text-racing-muted" data-testid="admin-user-email">{user.username || user.email} • {user.role}</span>
                        {["admin", "finance"].includes(user.role) && <NotificationsBell />}
                        <button
                            onClick={() => setCredentialsOpen(true)}
                            data-testid="admin-change-credentials-button"
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors border border-racing-line px-4 py-2"
                        >
                            <Key size={16} /> <span className="hidden md:inline">Username/Password</span>
                        </button>
                        <button
                            onClick={doLogout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors border border-racing-line px-4 py-2"
                        >
                            <SignOut size={16} /> Keluar
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-10">
                <nav className="space-y-7 mb-10" data-testid="admin-tabs">
                    {TAB_GROUPS.map((group) => {
                        const groupTabs = group.tabs.filter((item) => item.roles.includes(user.role));
                        if (groupTabs.length === 0) return null;
                        return (
                            <div key={group.id} data-testid={`admin-tab-group-${group.id}`}>
                                <p className="text-[10px] uppercase tracking-[0.3em] text-racing-golddeep mb-3 border-b border-racing-line pb-2">{group.label}</p>
                                <div className="flex flex-wrap gap-2">
                                    {groupTabs.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => setTab(t.id)}
                                            data-testid={`admin-tab-${t.id}`}
                                            className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-5 py-3 border transition-colors duration-300 ${
                                                tab === t.id
                                                    ? "bg-racing-red border-racing-red text-white"
                                                    : "border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/30"
                                            }`}
                                        >
                                            <t.icon size={16} weight="bold" /> {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </nav>

                {tab === "prestasi" && (
                    <>
                        <ParentAchievementsReview key={`review-${achievementRefreshKey}`} />
                        <EventMergeManager onMerged={() => setAchievementRefreshKey((key) => key + 1)} />
                        <ResourceTab
                        key={`achievements-${achievementRefreshKey}`}
                        title="Prestasi"
                        endpoint="achievements"
                        fetchKey="achievements"
                        testid="prestasi"
                        reorder
                        groupBy="event"
                        searchKey="athlete"
                        searchPlaceholder="Cari nama atlet..."
                        columns={[
                            { key: "athlete", label: "Atlet" },
                            { key: "title", label: "Nomor Lomba" },
                            { key: "event", label: "Event" },
                            { key: "level", label: "Tingkat" },
                            { key: "category", label: "Kategori" },
                            { key: "year", label: "Tahun" },
                        ]}
                        fields={[
                            { name: "athlete", label: "Nama Atlet", type: "athlete" },
                            { name: "title", label: "Nomor Lomba" },
                            { name: "event", label: "Nama Event (tersimpan — ketik sebagian lalu pilih dari daftar)", suggest: true },
                            { name: "year", label: "Tahun", type: "number", default: 2026 },
                            { name: "level", label: "Kejuaraan Tingkat", type: "select", options: [
                                { value: "Internal Klub", label: "Internal Klub" },
                                { value: "Kota/Kabupaten", label: "Kota/Kabupaten" },
                                { value: "Provinsi", label: "Provinsi" },
                                { value: "Nasional", label: "Nasional" },
                                { value: "Internasional", label: "Internasional" },
                            ], default: "Kota/Kabupaten" },
                            { name: "medal", label: "Medali", type: "select", options: MEDAL_OPTIONS, default: "gold" },
                            { name: "category", label: "Kategori", type: "select", options: [
                                { value: "Speed", label: "Speed" },
                                { value: "Standart", label: "Standart" },
                                { value: "Slalom", label: "Slalom" },
                            ], default: "Speed" },
                            { name: "image_url", label: "Foto Prestasi (Opsional)", type: "image", required: false, aspect: 4 / 3 },
                        ]}
                        />
                    </>
                )}

                {tab === "coach" && (
                    <ResourceTab
                        title="Coach"
                        endpoint="coaches"
                        fetchKey="coaches"
                        testid="coach"
                        columns={[
                            { key: "name", label: "Nama Panggilan" },
                            { key: "role", label: "Keterangan Besar" },
                        ]}
                        fields={[
                            { name: "name", label: "Nama Panggilan (mis. Coach Made)" },
                            { name: "role", label: "Keterangan Besar (mis. HEAD COACH)" },
                            { name: "description", label: "Keterangan / Deskripsi", type: "textarea" },
                            { name: "photo_url", label: "Foto Coach", type: "image", required: false, aspect: 4 / 5 },
                        ]}
                    />
                )}

                {tab === "jadwal" && (
                    <ResourceTab
                        title="Jadwal Latihan"
                        endpoint="schedules"
                        fetchKey="schedules"
                        testid="jadwal"
                        columns={[
                            { key: "group", label: "Kelompok" },
                            { key: "day", label: "Hari" },
                            { key: "time", label: "Jam" },
                            { key: "location", label: "Lokasi" },
                        ]}
                        fields={[
                            { name: "group", label: "Kelompok (mis. Pemula, Slalom)" },
                            { name: "day", label: "Hari (isi '-' jika hanya pengumuman)" },
                            { name: "time", label: "Jam (mis. 16.30)", required: false },
                            { name: "location", label: "Lokasi", required: false },
                            { name: "note", label: "Catatan (mis. Diumumkan di grup masing-masing)", required: false },
                        ]}
                    />
                )}

                {tab === "berita" && (
                    <ResourceTab
                        title="Berita"
                        endpoint="news"
                        fetchKey="news"
                        testid="berita"
                        columns={[
                            { key: "title", label: "Judul" },
                            { key: "category", label: "Kategori" },
                            { key: "date", label: "Tanggal" },
                        ]}
                        fields={[
                            { name: "title", label: "Judul Artikel" },
                            { name: "excerpt", label: "Ringkasan", type: "textarea" },
                            { name: "content", label: "Isi Artikel (pisahkan paragraf dengan baris kosong)", type: "textarea" },
                            { name: "category", label: "Kategori", default: "Berita" },
                            { name: "date", label: "Tanggal (YYYY-MM-DD)", default: "2026-01-01" },
                            { name: "image_url", label: "Gambar Artikel", type: "image", required: false, aspect: 4 / 3 },
                        ]}
                    />
                )}

                {tab === "gear" && (
                    <ResourceTab
                        title="Link Rekomendasi"
                        endpoint="shops"
                        fetchKey="shops"
                        testid="gear"
                        reorder
                        columns={[
                            { key: "name", label: "Nama Toko" },
                            { key: "tag", label: "Tag" },
                            { key: "url", label: "URL" },
                        ]}
                        fields={[
                            { name: "name", label: "Nama Toko" },
                            { name: "description", label: "Deskripsi", type: "textarea" },
                            { name: "url", label: "Link Toko" },
                            { name: "thumb_url", label: "Thumbnail Produk (terisi otomatis dari link jika bisa, atau upload manual)", type: "image", required: false, aspect: 1 / 1 },
                            { name: "tag", label: "Tag", default: "Toko" },
                        ]}
                    />
                )}

                {tab === "sponsor" && (
                    <ResourceTab
                        title="Sponsor"
                        endpoint="sponsors"
                        fetchKey="sponsors"
                        testid="sponsor"
                        columns={[
                            { key: "name", label: "Nama Sponsor" },
                            { key: "package", label: "Paket" },
                            { key: "website", label: "Website" },
                        ]}
                        fields={[
                            { name: "name", label: "Nama Sponsor" },
                            { name: "package", label: "Paket", type: "select", options: [
                                { value: "bulanan", label: "Bulanan" },
                                { value: "tahunan", label: "Tahunan" },
                            ], default: "bulanan" },
                            { name: "logo_url", label: "Logo Sponsor", type: "image", required: false, aspect: 3 / 2 },
                            { name: "website", label: "Website (opsional)", required: false },
                        ]}
                    />
                )}

                {tab === "atlet" && (
                    <ResourceTab
                        title="Atlet"
                        endpoint="athletes"
                        fetchKey="athletes"
                        testid="atlet"
                        reorder
                        statusToggle
                        searchKey="name"
                        searchPlaceholder="Cari nama atlet..."
                        columns={[
                            { key: "nia", label: "NIA" },
                            { key: "name", label: "Nama" },
                            { key: "class_name", label: "Kelas" },
                            { key: "birth_year", label: "Tahun Lahir" },
                            { key: "joined_year", label: "Bergabung" },
                            {
                                key: "status",
                                label: "Status",
                                render: (item) => (
                                    <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] border ${(item.status ?? "active") === "inactive" ? "border-racing-line text-racing-muted" : "border-racing-gold text-racing-golddeep bg-racing-gold/10"}`}>
                                        {(item.status ?? "active") === "inactive" ? "Nonaktif" : "Aktif"}
                                    </span>
                                ),
                            },
                        ]}
                        fields={[
                            { name: "nia", label: "Nomor Induk Atlet (NIA) — kosongkan untuk otomatis", required: false },
                            { name: "name", label: "Nama Lengkap" },
                            {
                                name: "class_name",
                                label: "Kelas",
                                type: "select",
                                required: false,
                                options: [
                                    { value: "", label: "— Belum Ada Kelas —" },
                                    { value: "Tim Hijau (Pemula)", label: "Tim Hijau (Pemula)" },
                                    { value: "Tim Kuning", label: "Tim Kuning" },
                                    { value: "Tim Jingga", label: "Tim Jingga" },
                                    { value: "Tim Biru", label: "Tim Biru" },
                                    { value: "Tim Pink", label: "Tim Pink" },
                                    { value: "Tim Merah", label: "Tim Merah" },
                                    { value: "Speed", label: "Speed" },
                                    { value: "Slalom", label: "Slalom" },
                                ],
                            },
                            { name: "birth_year", label: "Tahun Lahir", type: "number", default: 2010 },
                            { name: "joined_year", label: "Bergabung Sejak (Tahun)", type: "number", default: 2024 },
                            { name: "photo_url", label: "Foto Atlet", type: "image", required: false, aspect: 3 / 4 },
                        ]}
                    />
                )}

                {tab === "pengumuman" && (
                    <ResourceTab
                        title="Pengumuman"
                        endpoint="announcements"
                        fetchKey="announcements"
                        testid="pengumuman"
                        columns={[
                            { key: "title", label: "Judul" },
                            { key: "date", label: "Tanggal" },
                        ]}
                        fields={[
                            { name: "title", label: "Judul Pengumuman" },
                            { name: "body", label: "Isi Pengumuman (teks)", type: "textarea" },
                            { name: "date", label: "Tanggal (YYYY-MM-DD)", default: "2026-08-21" },
                            { name: "image_url", label: "Gambar Pengumuman (opsional)", type: "image", required: false, aspect: 4 / 3 },
                        ]}
                    />
                )}

                {tab === "galeri" && <GalleryTab />}

                {tab === "video" && (
                    <ResourceTab
                        title="Video"
                        endpoint="videos"
                        fetchKey="videos"
                        testid="video"
                        columns={[
                            { key: "title", label: "Judul" },
                            { key: "url", label: "Link" },
                        ]}
                        fields={[
                            { name: "title", label: "Judul Video" },
                            { name: "url", label: "Link YouTube (watch/shorts) atau Instagram (reel/post)" },
                        ]}
                    />
                )}

                {tab === "inquiries" && <InquiriesTab />}

                {tab === "parents" && <ParentPortalTab />}

                {tab === "daftar-ulang" && <ReregistrationTab />}

                {tab === "staff" && <StaffAccountsTab />}

                {tab === "iuran" && <FinanceDuesTab />}

                {tab === "pembayaran" && <PaymentSubmissionsTab />}

                {tab === "keuangan" && <FinanceReportTab />}

                {tab === "absensi" && <AttendanceTab />}
                {tab === "uang-latihan" && <FundSupportTab />}

                {tab === "rapot" && <ReportCardsTab />}

                {tab === "catatan-waktu" && <TimeRecordsTab />}
                {tab === "ranking" && <RankingsTab />}

                {tab === "backup" && <BackupTab />}

                {tab === "kehadiran-pelatih" && <CoachActivityTab />}

                {tab === "atlet-aktif" && <AdminAthletesTab />}

                {tab === "data-ganda" && <DuplicatesTab />}

                {tab === "stats" && <StatsTab />}

                {tab === "settings" && <SettingsTab />}

                <ChangeCredentialsModal open={credentialsOpen} onClose={() => setCredentialsOpen(false)} testid="admin-change-credentials" />
            </div>
        </div>
    );
}
